import React, { useEffect, useState, useRef } from 'react';
import { FileText, ExternalLink, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { reportApi } from '../../../services/reportApi';
import { useAskSidebar } from '../../../contexts/AskSidebarContext';
import type { ReportGeneratedWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: ReportGeneratedWidgetData;
}

export const ReportGeneratedWidget: React.FC<Props> = ({ data }) => {
  const { openReport } = useAskSidebar();
  const [status, setStatus] = useState<string>(data.status || 'generating');
  const [realProgress, setRealProgress] = useState<number>(0);
  const [displayProgress, setDisplayProgress] = useState<number>(0);
  const animationRef = useRef<ReturnType<typeof setInterval>>();

  // Poll backend for real progress
  useEffect(() => {
    if (status === 'generating') {
      const interval = setInterval(async () => {
        try {
          const prog = await reportApi.getGenerationProgress(data.reportId);
          if (prog) {
            setRealProgress(prog.progress);
            if (prog.complete) {
              setStatus('completed');
              setDisplayProgress(1);
              clearInterval(interval);
            } else if (prog.failed) {
              setStatus('failed');
              clearInterval(interval);
            }
          }
        } catch {
          // ignore polling errors
        }
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [status, data.reportId]);

  // Smoothly animate display progress towards (but not past) the next checkpoint
  useEffect(() => {
    if (status !== 'generating') return;

    // Determine the ceiling — animate up to 90% of the way to the next checkpoint
    // so it never appears stuck or reaches 100% prematurely
    const nextCheckpoint = realProgress < 0.2 ? 0.2 : realProgress < 0.8 ? 0.75 : 0.92;
    const ceiling = Math.max(realProgress, nextCheckpoint);

    if (animationRef.current) clearInterval(animationRef.current);

    animationRef.current = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= ceiling) return prev;
        // Slow down as we approach the ceiling (ease-out)
        const remaining = ceiling - prev;
        const increment = Math.max(0.005, remaining * 0.04);
        return Math.min(prev + increment, ceiling);
      });
    }, 500);

    return () => {
      if (animationRef.current) clearInterval(animationRef.current);
    };
  }, [status, realProgress]);

  const isGenerating = status === 'generating';
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';
  const progressPct = Math.round((isComplete ? 1 : displayProgress) * 100);

  return (
    <div className="rounded-lg bg-primary-50/50 dark:bg-primary-900/20 p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-800/50 flex items-center justify-center flex-shrink-0">
          <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {data.title}
          </p>

          {isGenerating && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-xs text-primary-600 dark:text-primary-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Generating draft — {progressPct}%</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full bg-primary-100 dark:bg-primary-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 dark:bg-primary-400 rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {isComplete && (
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
              <span className="text-xs text-green-600 dark:text-green-400">Draft ready</span>
            </div>
          )}

          {isFailed && (
            <div className="mt-2 flex items-center gap-2">
              <XCircle className="h-3.5 w-3.5 text-red-500" />
              <span className="text-xs text-red-600 dark:text-red-400">Generation failed</span>
            </div>
          )}
        </div>

        {(isComplete || isGenerating) && (
          <button
            onClick={() => openReport(data.reportId)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
              bg-primary-600 text-white hover:bg-primary-700
              dark:bg-primary-500 dark:hover:bg-primary-600
              transition-colors flex-shrink-0"
          >
            <ExternalLink className="h-3 w-3" />
            Open in Editor
          </button>
        )}
      </div>
    </div>
  );
};
